import type { LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '~/utils/cn';

interface CyclingIconProps {
  /** Ordered icons to rotate through; wraps back to the first after the last. */
  icons: LucideIcon[];
  /** Dwell time per icon (ms) before the next slides in. Default 2200. */
  intervalMs?: number;
  /** Classes applied to each rendered icon — size + color (e.g. `h-8 w-8 text-gray-800`). */
  iconClassName?: string;
  /** Classes for the clipping viewport; size it to match the icon so the slide is masked. */
  className?: string;
}

/**
 * Decorative icon carousel for processing/loading surfaces: the active icon
 * slides out to the left while the next slides in from the right, cycling on a
 * fixed interval. Both icons are stacked in the same grid cell and the viewport
 * clips the off-screen travel; the outgoing icon unmounts on `animationend`.
 * Purely visual (`aria-hidden`) — the surrounding element owns the a11y label.
 */
export function CyclingIcon({
  icons,
  intervalMs = 2200,
  iconClassName,
  className,
}: CyclingIconProps) {
  const [index, setIndex] = useState(0);
  // Index of the icon currently animating out; `null` when nothing is leaving.
  const [outgoing, setOutgoing] = useState<number | null>(null);
  const indexRef = useRef(0);

  useEffect(() => {
    // A single (or empty) icon set has nothing to cycle — render it static.
    if (icons.length <= 1) return;
    const id = window.setInterval(() => {
      const prev = indexRef.current;
      const next = (prev + 1) % icons.length;
      indexRef.current = next;
      setOutgoing(prev);
      setIndex(next);
    }, intervalMs);
    return () => {
      window.clearInterval(id);
    };
  }, [icons.length, intervalMs]);

  const CurrentIcon = icons[index];
  const OutgoingIcon = outgoing !== null ? icons[outgoing] : null;

  // Empty icon set — nothing to render. (Index access is otherwise in-range.)
  if (!CurrentIcon) return null;

  return (
    // Stack incoming + outgoing in one cell so they overlap mid-transition.
    <div
      aria-hidden="true"
      className={cn(
        'grid place-items-center overflow-hidden [&>*]:col-start-1 [&>*]:row-start-1',
        className,
      )}
    >
      {OutgoingIcon && (
        <OutgoingIcon
          key={`out-${String(outgoing)}`}
          className={cn('animate-icon-slide-out', iconClassName)}
          // Guard against a stale handler clearing a newer outgoing icon.
          onAnimationEnd={() => {
            setOutgoing((o) => (o === outgoing ? null : o));
          }}
        />
      )}
      <CurrentIcon
        key={`in-${String(index)}`}
        className={cn('animate-icon-slide-in', iconClassName)}
      />
    </div>
  );
}
